// FR-1 Tuning page. 参考 /home/loveinshare/code/violin/ 的展现形式:
// 每弦一行带 2 秒历史轨迹 canvas(低于阈值时冻结,自然存续不乱跳) + 指针式表盘
// (FR-1.2 行业标准形态,中心 ±5 cent 绿色容差弧) + 频率读数。
// 保留:音量阈值过滤(EMA 平滑)、音量条、阈值/平滑滑块、已调好特效与持久化。

import type { Page } from '../page';
import { el, TuningDial } from '../components';
import { state, toast } from '../store';
import { showMicError, showPermissionGuidance } from '../mic';
import { Tuner } from '../../tuning/tuner';
import { openStringFrequencies } from '../../data/pitches';
import { getState, patchState, tunedStringsSet, type TuningRecord } from '../../storage/api';
import type { StringType } from '../../types';

const STRING_LABELS = ['G', 'D', 'A', 'E'];
const STRING_NOTES = ['G3', 'D4', 'A4', 'E5'];
const TRAJ_MS = 2000;

export function tuningPage(): Page {
  let cleanup: (() => void) | null = null;
  return {
    async mount(container) {
      const backendState = await getState();
      let volumeThreshold = backendState.preferences.tuningVolumeThreshold;
      let smoothing = backendState.preferences.tuningSmoothing;
      let tuned = tunedStringsSet(backendState.tuning_results);

      const tuner = new Tuner(state.prefs.A4, volumeThreshold, smoothing);
      let manual: StringType | null = null;
      let unsub: (() => void) | null = null;

      // ---- 频率读数 ----
      const freqBig = el('span', { class: 'big', text: '—' });
      const nearestEl = el('div', { class: 'nearest', text: '等待琴声…' });
      const freqReadout = el('div', { class: 'freq-readout' }, [el('div', {}, [freqBig, el('span', { class: 'unit', text: ' Hz' })]), nearestEl]);

      // ---- 弦区:4 个卡片,各含轨迹 canvas ----
      const trajData: { time: number; cents: number }[][] = [[], [], [], []];
      const canvases: HTMLCanvasElement[] = [];
      const strCards: HTMLElement[] = [];
      STRING_LABELS.forEach((label, i) => {
        const cv = el('canvas', { class: 'str-traj' }) as HTMLCanvasElement;
        canvases.push(cv);
        const card = el('div', { class: 'str-card' + (tuned.has(STRING_LABELS[i]) ? ' tuned' : '') }, [
          el('div', { class: 'str-head' }, [
            el('span', { class: 'str-name', text: label }),
            el('span', { class: 'str-note', text: STRING_NOTES[i] }),
          ]),
          cv,
        ]);
        strCards.push(card);
      });
      const stringGrid = el('div', { class: 'string-grid' }, strCards);

      // ---- 指针式表盘(FR-1.2:行业标准形态 + ±5 cent 绿色容差弧) ----
      const dial = new TuningDial();
      const dialWrap = el('div', { class: 'dial-wrap' }, [dial.el]);

      // ---- 音量条 ----
      const volFill = el('div', { class: 'fill' });
      const volThresh = el('div', { class: 'thresh' });
      const volbar = el('div', { class: 'volbar' }, [volFill, volThresh]);
      const volLabel = el('span', { class: 'tiny muted', text: '音量 0% · 阈值 0%' });

      // ---- 阈值/平滑滑块 ----
      const thrSlider = el('input', { type: 'range', min: '0', max: '100', step: '1', value: String(volumeThreshold) });
      const thrVal = el('span', { class: 'tiny muted', text: `阈值 ${volumeThreshold}%` });
      thrSlider.oninput = () => {
        volumeThreshold = Number(thrSlider.value);
        tuner.setVolumeThreshold(volumeThreshold);
        thrVal.textContent = `阈值 ${volumeThreshold}%`;
        updateVolMarker();
        persistPrefs();
      };
      const smoothSlider = el('input', { type: 'range', min: '0', max: '100', step: '1', value: String(smoothing) });
      const smoothVal = el('span', { class: 'tiny muted', text: `平滑 ${smoothing}%` });
      smoothSlider.oninput = () => {
        smoothing = Number(smoothSlider.value);
        tuner.setSmoothing(smoothing);
        smoothVal.textContent = `平滑 ${smoothing}%`;
        persistPrefs();
      };
      let persistTimer: number | null = null;
      function persistPrefs() {
        if (persistTimer) window.clearTimeout(persistTimer);
        persistTimer = window.setTimeout(() => {
          patchState({ preferences: { tuningVolumeThreshold: volumeThreshold, tuningSmoothing: smoothing } }).catch(() => undefined);
        }, 400);
      }
      function updateVolMarker() { volThresh.style.left = `${volumeThreshold}%`; }
      updateVolMarker();

      // ---- 弦选择 ----
      const chips = STRING_LABELS.map((label, i) => {
        const c = el('button', { class: 'chip', text: `${label} 弦` });
        c.onclick = () => {
          const idx = i as StringType;
          if (manual === idx) {
            manual = null; tuner.setManualString(null); c.classList.remove('active'); autoChip.classList.add('active');
          } else {
            manual = idx; tuner.setManualString(idx);
            chips.forEach((x) => x.classList.remove('active')); autoChip.classList.remove('active'); c.classList.add('active');
          }
        };
        return c;
      });
      const autoChip = el('button', { class: 'chip active', text: '自动识别' });
      autoChip.onclick = () => { manual = null; tuner.setManualString(null); chips.forEach((x) => x.classList.remove('active')); autoChip.classList.add('active'); };
      const stringRow = el('div', { class: 'row wrap center' }, [autoChip, ...chips]);

      // ---- 已调好指示 ----
      const pills = STRING_LABELS.map((label, i) =>
        el('div', { class: 'str-pill' + (tuned.has(STRING_LABELS[i]) ? ' tuned' : '') }, [
          el('div', { text: label, style: 'font-weight:600;font-size:16px' }),
          el('div', { class: 'check', text: '✓ 已调好' }),
        ]),
      );
      const progressEl = el('div', { class: 'tiny muted' });
      const resetBtn = el('button', { class: 'chip', text: '重置已调好' });
      resetBtn.onclick = async () => {
        tuned.clear(); renderTuned(true);
        patchState({ tuning_results: STRING_LABELS.map((s) => ({ string: s, tuned: false, cents: 0, timestamp: Date.now() })) }).catch(() => undefined);
        celebrateEl.style.display = 'none'; toast('已重置');
      };
      const celebrateEl = el('div', { class: 'celebrate', style: 'display:none', text: '🎉 四弦全部调好！可以开始练习了' });
      function renderTuned(animateAll = false) {
        pills.forEach((p, i) => {
          const isTuned = tuned.has(STRING_LABELS[i]);
          const was = p.classList.contains('tuned');
          p.classList.toggle('tuned', isTuned);
          if (isTuned && (!was || animateAll)) { p.classList.remove('just-tuned'); void p.offsetWidth; p.classList.add('just-tuned'); }
        });
        progressEl.textContent = `${tuned.size}/4 已调好`;
        celebrateEl.style.display = tuned.size === 4 ? '' : 'none';
      }
      renderTuned();
      async function markTuned(stringIdx: StringType, cents: number) {
        const key = STRING_LABELS[stringIdx];
        if (tuned.has(key)) return;
        tuned.add(key); renderTuned();
        const rec: TuningRecord = { string: key, tuned: true, cents, timestamp: Date.now() };
        patchState({ tuning_results: [rec] }).catch(() => undefined);
        toast(`🎉 ${key} 弦已调好！(${tuned.size}/4)`);
      }

      // ---- A4 ----
      const a4Val = el('span', { text: `${state.prefs.A4.toFixed(1)} Hz` });
      const a4Minus = el('button', { class: 'chip', text: '−0.1' });
      const a4Plus = el('button', { class: 'chip', text: '+0.1' });
      a4Minus.onclick = () => { state.setPrefs({ A4: Math.max(410, +(state.prefs.A4 - 0.1).toFixed(1)) }); tuner.setA4(state.prefs.A4); a4Val.textContent = `${state.prefs.A4.toFixed(1)} Hz`; };
      a4Plus.onclick = () => { state.setPrefs({ A4: Math.min(470, +(state.prefs.A4 + 0.1).toFixed(1)) }); tuner.setA4(state.prefs.A4); a4Val.textContent = `${state.prefs.A4.toFixed(1)} Hz`; };

      // ---- 参考音 ----
      let refOsc: { osc: OscillatorNode; gain: GainNode } | null = null;
      let refString = 2;
      let refGainPct = 30; // 0..100
      const refToggle = el('button', { class: 'chip', text: '🎵 参考音' });
      const refVolSlider = el('input', { type: 'range', min: '0', max: '100', step: '1', value: String(refGainPct), class: 'ref-vol' }) as HTMLInputElement;
      const refVolLabel = el('span', { class: 'tiny muted', text: `音量 ${refGainPct}%` });
      refVolSlider.oninput = () => {
        refGainPct = Number(refVolSlider.value);
        refVolLabel.textContent = `音量 ${refGainPct}%`;
        if (refOsc) refOsc.gain.gain.value = refGainPct / 100 * 0.4; // 上限 0.4 防爆音
      };
      refToggle.onclick = () => {
        if (refOsc) { refOsc.osc.stop(); refOsc = null; refToggle.classList.remove('active'); }
        else { startRef(); refToggle.classList.add('active'); }
      };
      const refStringBtns = STRING_LABELS.map((label, i) => {
        const b = el('button', { class: 'chip' + (i === refString ? ' active' : ''), text: label });
        b.onclick = () => { refString = i; refStringBtns.forEach((x) => x.classList.remove('active')); b.classList.add('active'); if (refOsc) startRef(); };
        return b;
      });
      async function startRef() {
        const ctx = await state.engine.ensureAudio();
        if (!ctx) return;
        if (refOsc) { refOsc.osc.stop(); refOsc = null; }
        const targets = openStringFrequencies(state.prefs.A4);
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine'; osc.frequency.value = targets[refString]; gain.gain.value = refGainPct / 100 * 0.4;
        osc.connect(gain).connect(ctx.destination); osc.start(); refOsc = { osc, gain };
      }

      const statusEl = el('div', { class: 'tiny muted', text: '点击开始调音，允许麦克风权限' });
      const startBtn = el('button', { class: 'primary', text: '开始调音' });
      const stopBtn = el('button', { class: 'ghost', text: '停止', style: 'display:none' });
      let tunerLastCents = 0;
      tuner.onLocked = (stringIdx) => { markTuned(stringIdx, tunerLastCents); };

      // ---- 轨迹绘制(2秒窗口,低于阈值时冻结) ----
      const dpr = window.devicePixelRatio || 1;
      function drawTraj(idx: number) {
        const cv = canvases[idx];
        const ctx = cv.getContext('2d');
        if (!ctx) return;
        const w = cv.clientWidth, h = cv.clientHeight;
        cv.width = w * dpr; cv.height = h * dpr;
        ctx.clearRect(0, 0, cv.width, cv.height);
        // 中心准音线 + ±5音分绿带
        ctx.strokeStyle = 'rgba(124,92,255,.3)'; ctx.lineWidth = 1;
        ctx.beginPath(); ctx.moveTo(0, cv.height / 2); ctx.lineTo(cv.width, cv.height / 2); ctx.stroke();
        const data = trajData[idx];
        if (data.length < 2) return;
        const now = performance.now();
        const half = cv.height / 2 - 2 * dpr;
        for (let i = 1; i < data.length; i++) {
          const p0 = data[i - 1], p1 = data[i];
          const x0 = cv.width - ((now - p0.time) / TRAJ_MS) * cv.width;
          const x1 = cv.width - ((now - p1.time) / TRAJ_MS) * cv.width;
          const y0 = cv.height / 2 - Math.max(-1, Math.min(1, p0.cents / 50)) * half;
          const y1 = cv.height / 2 - Math.max(-1, Math.min(1, p1.cents / 50)) * half;
          ctx.strokeStyle = p1.cents < 0 ? '#3ddc84' : p1.cents > 0 ? '#ff5470' : '#f5c542';
          ctx.lineWidth = 2 * dpr;
          ctx.beginPath(); ctx.moveTo(x0, y0); ctx.lineTo(x1, y1); ctx.stroke();
        }
      }
      let trajRaf = 0;
      function trajLoop() {
        for (let i = 0; i < 4; i++) drawTraj(i);
        trajRaf = requestAnimationFrame(trajLoop);
      }

      const card = el('div', { class: 'card' }, [
        el('div', { class: 'row center', style: 'margin-bottom:8px' }, [startBtn, stopBtn]),
        freqReadout,
        stringGrid,
        dialWrap,
        el('h2', { text: '实时音量' }),
        volbar,
        el('div', { class: 'row between' }, [volLabel, el('span', { class: 'tiny muted', text: '▾ = 阈值' })]),
        el('h2', { text: '已调好' }),
        el('div', { class: 'row between center' }, [el('div', { class: 'row', style: 'gap:8px' }, pills), progressEl]),
        celebrateEl,
        el('div', { style: 'margin-top:8px' }, [resetBtn]),
        el('h2', { text: '调音参数' }),
        el('label', { class: 'field' }, [thrVal, thrSlider]),
        el('label', { class: 'field' }, [smoothVal, smoothSlider]),
        el('p', { class: 'tiny muted', text: '阈值:音量低于此值不识别(冻结轨迹)。平滑:越大指针越稳、响应略慢。' }),
        el('h2', { text: '目标弦' }),
        stringRow,
        el('h2', { text: 'A4 参考频率' }),
        el('div', { class: 'row center' }, [a4Minus, a4Val, a4Plus]),
        el('p', { class: 'tiny muted', text: '范围 410–470 Hz，步进 0.1 Hz。可在设置页精细调整。' }),
        el('h2', { text: '参考音' }),
        el('div', { class: 'row wrap' }, refStringBtns),
        el('div', { class: 'row center', style: 'margin-top:8px;gap:10px;flex-wrap:wrap' }, [
          refToggle,
          el('div', { class: 'ref-vol-wrap' }, [refVolSlider, refVolLabel]),
        ]),
      ]);

      async function start() {
        startBtn.disabled = true;
        try {
          await state.engine.start();
          tuner.setA4(state.prefs.A4);
          tuner.setVolumeThreshold(volumeThreshold);
          tuner.setSmoothing(smoothing);
          tuner.reset();
          for (let i = 0; i < 4; i++) trajData[i] = [];
          statusEl.textContent = '聆听中…拉响一根弦';
          startBtn.style.display = 'none'; stopBtn.style.display = '';
          trajLoop();
          unsub = state.engine.onFrameEvent((f) => {
            const volPct = Math.min(100, (f.rms / 0.2) * 100);
            volFill.style.width = `${volPct}%`;
            volLabel.textContent = `音量 ${volPct.toFixed(0)}% · 阈值 ${volumeThreshold}%`;
            const r = tuner.process(f);
            tunerLastCents = r.centsDeviation;
            const loud = f.rms >= (volumeThreshold / 100) * 0.2;
            if (r.activeString !== null && r.status !== 'capturing' && loud) {
              // 有有效信号:高亮弦卡 + 记录轨迹 + 更新表盘/频率
              strCards.forEach((c) => c.classList.remove('active'));
              strCards[r.activeString].classList.add('active');
              freqBig.textContent = f.freq.toFixed(1);
              nearestEl.innerHTML = `最近: <span style="color:var(--accent-2)">${STRING_LABELS[r.activeString]}</span> (${STRING_NOTES[r.activeString]})`;
              trajData[r.activeString].push({ time: performance.now(), cents: r.centsDeviation });
              trajData[r.activeString] = trajData[r.activeString].filter((p) => performance.now() - p.time < TRAJ_MS);
              dial.setNeedle(r.centsDeviation, `${STRING_LABELS[r.activeString]} 弦 · ${STRING_NOTES[r.activeString]}`, r.confidence);
            } else {
              // 低于阈值:冻结显示(canvas 自然保留最后2秒)
              strCards.forEach((c) => c.classList.remove('active'));
              dial.setNeedle(null, '—', 0);
            }
            statusEl.textContent = statusText(r.status);
          });
        } catch (e) {
          startBtn.disabled = false;
          if (showMicError(e)) showPermissionGuidance();
        }
      }
      function stop() {
        unsub?.(); unsub = null;
        cancelAnimationFrame(trajRaf);
        if (refOsc) { refOsc.osc.stop(); refOsc = null; refToggle.classList.remove('active'); }
        state.engine.stop();
        startBtn.style.display = ''; startBtn.disabled = false; stopBtn.style.display = 'none';
        statusEl.textContent = '已停止';
        volFill.style.width = '0%'; volLabel.textContent = `音量 0% · 阈值 ${volumeThreshold}%`;
        strCards.forEach((c) => c.classList.remove('active'));
        dial.setNeedle(null, '—', 0);
      }
      startBtn.onclick = start;
      stopBtn.onclick = stop;

      container.append(
        el('div', { class: 'row between', style: 'align-items:baseline' }, [
          el('div', {}, [el('h1', { text: '智能调音' }), el('p', { class: 'sub', text: '自动识别 G/D/A/E 四弦，实时 cent 偏差' })]),
          statusEl,
        ]),
        card,
        el('p', { class: 'tiny muted', text: '🔒 音频仅在本地处理；调音参数与已调好记录存后端(未来转数据库)。' }),
      );
      cleanup = stop;
    },
    unmount() {
      cleanup?.();
      cleanup = null;
    },
  };
}

function statusText(status: string): string {
  switch (status) {
    case 'capturing': return '等待琴声(音量不足)…';
    case 'analyzing': return '识别中…';
    case 'stable': return '接近准音…';
    case 'locked': return '已调好 ✓';
    default: return '';
  }
}
